<template>
  <div class="bank-approvals">
    <div class="page-header">
      <h2>🏦 تایید اطلاعات بانکی سالن‌ها</h2>
      <p class="subtitle">مدیریت و تایید اطلاعات بانکی سالن‌ها</p>
    </div>

    <!-- ========================================== -->
    <!-- فیلترها -->
    <!-- ========================================== -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>وضعیت</label>
          <select v-model="filters.status" @change="loadData">
            <option value="">همه</option>
            <option value="pending">در انتظار تایید</option>
            <option value="approved">تایید شده</option>
            <option value="rejected">رد شده</option>
          </select>
        </div>

        <div class="filter-group">
          <label>جستجو</label>
          <input
              v-model="filters.search"
              type="text"
              placeholder="نام سالن، کد ملی یا شبا..."
              class="filter-input"
              @keyup.enter="loadData"
          />
        </div>

        <button class="btn-search" @click="loadData">🔍 جستجو</button>
        <button class="btn-reset" @click="resetFilters">⟳ پاک کردن</button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- لیست درخواست‌ها -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else-if="items.length === 0" class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>هیچ درخواستی یافت نشد</h3>
    </div>

    <div v-else>
      <!-- ===== دسکتاپ: جدول ===== -->
      <div class="desktop-table">
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>سالن</th>
            <th>کد ملی</th>
            <th>تاریخ تولد</th>
            <th>شماره شبا</th>
            <th>عکس کارت ملی</th>
            <th>وضعیت</th>
            <th>عملیات</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in items" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td class="market-name">{{ getMarketName(item) }}</td>
            <td class="national-code">{{ item.national_code || '-' }}</td>
            <td>{{ item.birth_date || '-' }}</td>
            <td class="iban-cell">{{ item.iban || '-' }}</td>
            <td>
              <img
                  v-if="item.card_image_front"
                  :src="item.card_image_front"
                  alt="کارت ملی"
                  class="card-thumb"
                  @click="openImage(item.card_image_front)"
              />
              <span v-else class="text-muted">بدون عکس</span>
            </td>
            <td>
                <span :class="['badge', statusClass(item.status)]">
                  {{ statusLabel(item.status) }}
                </span>
              <div v-if="item.status === 'rejected' && item.reject_reason" class="reject-reason">
                <small>دلیل: {{ item.reject_reason }}</small>
              </div>
            </td>
            <td>
              <div class="btn-group">
                <!-- دکمه استعلام -->
                <button
                    v-if="item.status === 'pending'"
                    class="btn-verify"
                    @click="verify(item)"
                    :disabled="verifyingId === item.id"
                >
                  {{ verifyingId === item.id ? '⏳...' : '🔍 استعلام' }}
                </button>

                <!-- دکمه تایید (فقط اگر استعلام موفق بود) -->
                <button
                    v-if="item.status === 'pending' && item.can_approve"
                    class="btn-approve"
                    @click="approve(item)"
                >
                  ✅ تایید
                </button>

                <!-- دکمه رد -->
                <button
                    v-if="item.status === 'pending'"
                    class="btn-reject"
                    @click="openRejectModal(item)"
                >
                  ❌ رد
                </button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ===== موبایل: کارت ===== -->
      <div class="mobile-cards">
        <div v-for="(item, index) in items" :key="item.id" class="card-item">
          <div class="card-header">
            <span class="card-index">#{{ index + 1 }}</span>
            <span :class="['badge', statusClass(item.status)]">
              {{ statusLabel(item.status) }}
            </span>
          </div>
          <div class="card-body">
            <div class="card-row">
              <span class="card-label">سالن:</span>
              <span>{{ getMarketName(item) }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">کد ملی:</span>
              <span>{{ item.national_code || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">تاریخ تولد:</span>
              <span>{{ item.birth_date || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">شبا:</span>
              <span class="iban-cell">{{ item.iban || '-' }}</span>
            </div>
            <div class="card-row" v-if="item.card_image_front">
              <span class="card-label">کارت ملی:</span>
              <img :src="item.card_image_front" class="card-thumb" @click="openImage(item.card_image_front)" />
            </div>
            <div v-if="item.status === 'rejected' && item.reject_reason" class="card-row">
              <span class="card-label">دلیل رد:</span>
              <span class="reject-text">{{ item.reject_reason }}</span>
            </div>
          </div>
          <div class="card-actions">
            <button
                v-if="item.status === 'pending'"
                class="btn-verify"
                @click="verify(item)"
                :disabled="verifyingId === item.id"
            >
              {{ verifyingId === item.id ? '⏳...' : '🔍 استعلام' }}
            </button>
            <button
                v-if="item.status === 'pending' && item.can_approve"
                class="btn-approve"
                @click="approve(item)"
            >
              ✅ تایید
            </button>
            <button
                v-if="item.status === 'pending'"
                class="btn-reject"
                @click="openRejectModal(item)"
            >
              ❌ رد
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- مودال نتایج استعلام -->
    <!-- ========================================== -->
    <div v-if="showVerifyModal" class="modal-overlay" @click.self="closeVerifyModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>🔍 نتایج استعلام</h3>
          <button class="btn-close" @click="closeVerifyModal">✕</button>
        </div>
        <div class="modal-body">
          <div v-if="verifyResult">
            <!-- وضعیت نهایی -->
            <div :class="['verify-status', verifyResult.can_approve ? 'verify-success' : 'verify-failed']">
              <strong>
                {{ verifyResult.can_approve
                  ? '✅ همه استعلام‌ها موفق بودند. می‌توانید تایید کنید.'
                  : '⚠️ برخی استعلام‌ها ناموفق بودند.'
                }}
              </strong>
            </div>

            <!-- ۱. تطبیق موبایل با کد ملی -->
            <div class="verify-card">
              <h4>۱. تطبیق شماره موبایل با کد ملی</h4>
              <div class="verify-row">
                <span>شماره موبایل:</span>
                <span class="value">{{ verifyResult.market_phone || 'ثبت نشده' }}</span>
              </div>
              <div class="verify-row">
                <span>نتیجه:</span>
                <span :class="verifyResult.mobile_match && verifyResult.mobile_match.data ? 'text-success' : 'text-danger'">
                  {{ verifyResult.mobile_match && verifyResult.mobile_match.data ? '✅ تطابق دارد' : '❌ تطابق ندارد' }}
                </span>
              </div>
              <div v-if="verifyResult.mobile_match && verifyResult.mobile_match.message" class="verify-message">
                {{ verifyResult.mobile_match.message }}
              </div>
            </div>

            <!-- ۲. تطبیق شبا با کد ملی -->
            <div class="verify-card">
              <h4>۲. تطبیق شماره شبا با کد ملی</h4>
              <div class="verify-row">
                <span>نتیجه:</span>
                <span :class="verifyResult.iban_match && verifyResult.iban_match.data ? 'text-success' : 'text-danger'">
                  {{ verifyResult.iban_match && verifyResult.iban_match.data ? '✅ تطابق دارد' : '❌ تطابق ندارد' }}
                </span>
              </div>
              <div v-if="verifyResult.iban_match && verifyResult.iban_match.message" class="verify-message">
                {{ verifyResult.iban_match.message }}
              </div>
            </div>

            <!-- ۳. استعلام اطلاعات شبا -->
            <div class="verify-card">
              <h4>۳. استعلام اطلاعات شبا</h4>
              <div v-if="verifyResult.iban_info && verifyResult.iban_info.data">
                <div class="verify-row">
                  <span>نام دارنده:</span>
                  <span class="value">{{ verifyResult.iban_info.data.name || '—' }}</span>
                </div>
                <div class="verify-row">
                  <span>نام بانک:</span>
                  <span class="value">{{ verifyResult.iban_info.data.bank_name || '—' }}</span>
                </div>
                <div class="verify-row">
                  <span>وضعیت:</span>
                  <span :class="verifyResult.iban_info.data.active ? 'text-success' : 'text-danger'">
                    {{ verifyResult.iban_info.data.active ? '✅ فعال' : '❌ غیرفعال' }}
                  </span>
                </div>
              </div>
              <div v-else class="text-danger">
                ❌ خطا در استعلام اطلاعات شبا
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeVerifyModal">بستن</button>
          <button
              v-if="verifyResult && verifyResult.can_approve"
              class="btn-approve"
              @click="approveFromModal"
          >
            ✅ تایید اطلاعات
          </button>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- مودال رد با دلیل -->
    <!-- ========================================== -->
    <div v-if="showRejectModal" class="modal-overlay" @click.self="closeRejectModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>❌ رد درخواست</h3>
          <button class="btn-close" @click="closeRejectModal">✕</button>
        </div>
        <div class="modal-body">
          <p class="modal-info">
            در حال رد درخواست سالن:
            <strong>{{ selectedItem ? getMarketName(selectedItem) : 'نامشخص' }}</strong>
          </p>
          <div class="form-group">
            <label class="required">دلیل رد</label>
            <textarea
                v-model="rejectReason"
                class="form-textarea"
                placeholder="دلیل رد را وارد کنید..."
                rows="4"
                maxlength="500"
            ></textarea>
            <small class="help-text">{{ rejectReason.length }}/۵۰۰</small>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeRejectModal">انصراف</button>
          <button
              class="btn-reject"
              @click="confirmReject"
              :disabled="!rejectReason.trim() || submitting"
          >
            {{ submitting ? '⏳ در حال ثبت...' : 'تأیید رد' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- مودال نمایش عکس بزرگ -->
    <!-- ========================================== -->
    <div v-if="showImageModal" class="image-modal-overlay" @click.self="closeImageModal">
      <div class="image-modal-content">
        <button class="image-close" @click="closeImageModal">✕</button>
        <img :src="selectedImage" alt="کارت ملی" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BankApprovals',

  data: function() {
    return {
      loading: false,
      submitting: false,
      verifyingId: null,
      items: [],
      filters: {
        status: 'pending',
        search: ''
      },
      showVerifyModal: false,
      showRejectModal: false,
      showImageModal: false,
      verifyResult: null,
      selectedItem: null,
      selectedImage: null,
      rejectReason: ''
    };
  },

  mounted: function() {
    this.loadData();
  },

  methods: {
    // ==========================================
    // دریافت نام سالن از آبجکت
    // ==========================================
    getMarketName: function(item) {
      if (!item) return 'نامشخص';

      // اگر market_name یک آبجکت باشد
      if (item.market_name && typeof item.market_name === 'object') {
        return item.market_name.title || 'نامشخص';
      }

      // اگر market_name رشته باشد
      if (typeof item.market_name === 'string') {
        return item.market_name;
      }

      // اگر market یک آبجکت باشد
      if (item.market && item.market.title) {
        return item.market.title;
      }

      return 'نامشخص';
    },

    // ==========================================
    // بارگذاری داده‌ها
    // ==========================================
    loadData: async function() {
      this.loading = true;
      try {
        var params = {};
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.search) params.search = this.filters.search;

        var response = await axios.get('/api/super-admin/bank-accounts', { params: params });
        var data = response.data.data || [];

        // اضافه کردن can_approve به هر آیتم
        var self = this;
        this.items = data.map(function(item) {
          return Object.assign({}, item, {
            can_approve: false
          });
        });

      } catch (error) {
        console.error('خطا:', error);
        if (this.$message) {
          this.$message.error('خطا در بارگذاری اطلاعات');
        } else {
          alert('خطا در بارگذاری اطلاعات');
        }
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // استعلام سه سرویس
    // ==========================================
    verify: async function(item) {
      this.verifyingId = item.id;
      this.verifyResult = null;

      try {
        var response = await axios.post('/api/super-admin/bank-account/' + item.id + '/verify');
        var data = response.data;

        // ذخیره نتیجه
        this.verifyResult = data;
        this.showVerifyModal = true;

        // بروزرسانی can_approve در لیست
        for (var i = 0; i < this.items.length; i++) {
          if (this.items[i].id === item.id) {
            this.$set(this.items[i], 'can_approve', data.can_approve);
            break;
          }
        }

      } catch (error) {
        var errorMsg = 'خطا در استعلام';
        if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        }

        if (this.$message) {
          this.$message.error(errorMsg);
        } else {
          alert(errorMsg);
        }
        console.error(error);
      } finally {
        this.verifyingId = null;
      }
    },

    // ==========================================
    // تایید
    // ==========================================
    approve: async function(item) {
      if (!item.can_approve) {
        if (this.$message) {
          this.$message.warning('لطفاً ابتدا استعلام را انجام دهید.');
        } else {
          alert('لطفاً ابتدا استعلام را انجام دهید.');
        }
        return;
      }

      var marketName = this.getMarketName(item);

      if (!confirm('آیا از تایید اطلاعات بانکی سالن "' + marketName + '" اطمینان دارید؟')) {
        return;
      }

      try {
        await axios.post('/api/super-admin/bank-account/' + item.market_id + '/approve');

        if (this.$message) {
          this.$message.success('اطلاعات بانکی با موفقیت تایید شد.');
        } else {
          alert('اطلاعات بانکی با موفقیت تایید شد.');
        }

        this.loadData();
      } catch (error) {
        var errorMsg = 'خطا در تایید';
        if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        }

        if (this.$message) {
          this.$message.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      }
    },

    // ==========================================
    // تایید از داخل مودال
    // ==========================================
    approveFromModal: function() {
      var self = this;
      var targetItem = null;

      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].can_approve) {
          targetItem = this.items[i];
          break;
        }
      }

      this.closeVerifyModal();

      if (targetItem) {
        self.approve(targetItem);
      }
    },

    // ==========================================
    // رد
    // ==========================================
    openRejectModal: function(item) {
      this.selectedItem = item;
      this.rejectReason = '';
      this.showRejectModal = true;
    },

    closeRejectModal: function() {
      this.showRejectModal = false;
      this.selectedItem = null;
      this.rejectReason = '';
    },

    confirmReject: async function() {
      if (!this.rejectReason.trim()) {
        if (this.$message) {
          this.$message.warning('لطفاً دلیل رد را وارد کنید.');
        } else {
          alert('لطفاً دلیل رد را وارد کنید.');
        }
        return;
      }

      this.submitting = true;
      try {
        await axios.post('/api/super-admin/bank-account/' + this.selectedItem.market_id + '/reject', {
          reason: this.rejectReason.trim()
        });

        if (this.$message) {
          this.$message.success('درخواست با موفقیت رد شد.');
        } else {
          alert('درخواست با موفقیت رد شد.');
        }

        this.closeRejectModal();
        this.loadData();
      } catch (error) {
        var errorMsg = 'خطا در رد درخواست';
        if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        }

        if (this.$message) {
          this.$message.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        this.submitting = false;
      }
    },

    // ==========================================
    // مودال استعلام
    // ==========================================
    closeVerifyModal: function() {
      this.showVerifyModal = false;
      this.verifyResult = null;
    },

    // ==========================================
    // نمایش عکس
    // ==========================================
    openImage: function(imageUrl) {
      this.selectedImage = imageUrl;
      this.showImageModal = true;
    },

    closeImageModal: function() {
      this.showImageModal = false;
      this.selectedImage = null;
    },

    // ==========================================
    // ریست فیلترها
    // ==========================================
    resetFilters: function() {
      this.filters = { status: '', search: '' };
      this.loadData();
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    statusLabel: function(status) {
      var map = {
        pending: 'در انتظار تایید',
        approved: 'تایید شده',
        rejected: 'رد شده'
      };
      return map[status] || status;
    },

    statusClass: function(status) {
      var map = {
        pending: 'badge-warning',
        approved: 'badge-success',
        rejected: 'badge-danger'
      };
      return map[status] || 'badge-gray';
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.bank-approvals {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  direction: rtl;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.page-header .subtitle {
  margin: 4px 0 0 0;
  color: #909399;
  font-size: 14px;
}

/* ========================================== */
/* فیلترها */
/* ========================================== */
.filter-section {
  background: white;
  padding: 16px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-group select,
.filter-input {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
}

.filter-input {
  min-width: 180px;
}

.filter-input:focus,
.filter-group select:focus {
  outline: none;
  border-color: #409eff;
}

.btn-search {
  padding: 6px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-search:hover {
  background: #66b1ff;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-reset:hover {
  background: #e4e7ed;
}

/* ========================================== */
/* حالت بارگذاری و خالی */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 10px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 10px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 4px;
}

/* ========================================== */
/* جدول */
/* ========================================== */
.desktop-table {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  overflow-x: auto;
}

.desktop-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 900px;
}

.desktop-table thead th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 12px 14px;
  text-align: right;
  border-bottom: 2px solid #e4e7ed;
  white-space: nowrap;
}

.desktop-table tbody td {
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  vertical-align: middle;
}

.desktop-table tbody tr:hover td {
  background: #f5f7fa;
}

.market-name {
  font-weight: 500;
}

.national-code {
  font-family: monospace;
  direction: ltr;
}

.iban-cell {
  font-family: monospace;
  font-size: 12px;
  direction: ltr;
}

.text-muted {
  color: #c0c4cc;
}

.reject-reason small {
  color: #f56c6c;
  display: block;
  font-size: 11px;
  margin-top: 2px;
}

.reject-text {
  color: #f56c6c;
}

/* ========================================== */
/* عکس کارت ملی */
/* ========================================== */
.card-thumb {
  width: 60px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: transform 0.2s;
}

.card-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* ========================================== */
/* بج‌ها */
/* ========================================== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.badge-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.badge-gray {
  background: #f4f4f5;
  color: #909399;
}

/* ========================================== */
/* دکمه‌های عملیات */
/* ========================================== */
.btn-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.btn-verify {
  padding: 4px 12px;
  background: #ecf5ff;
  color: #409eff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-verify:hover:not(:disabled) {
  background: #409eff;
  color: white;
}

.btn-verify:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-approve {
  padding: 4px 12px;
  background: #f0f9eb;
  color: #67c23a;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-approve:hover {
  background: #67c23a;
  color: white;
}

.btn-reject {
  padding: 4px 12px;
  background: #fef0f0;
  color: #f56c6c;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-reject:hover:not(:disabled) {
  background: #f56c6c;
  color: white;
}

.btn-reject:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================== */
/* کارت‌های موبایل */
/* ========================================== */
.mobile-cards {
  display: none;
}

.card-item {
  background: white;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.card-index {
  font-weight: 600;
  color: #909399;
  font-size: 13px;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-row {
  display: flex;
  gap: 6px;
  font-size: 13px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 70px;
}

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.card-actions .btn-verify,
.card-actions .btn-approve,
.card-actions .btn-reject {
  flex: 1;
  padding: 8px;
  text-align: center;
}

/* ========================================== */
/* مودال */
/* ========================================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #909399;
}

.btn-close:hover {
  color: #2c3e50;
}

.modal-body {
  padding: 20px;
}

.modal-info {
  margin-bottom: 16px;
  color: #606266;
}

.modal-info strong {
  color: #2c3e50;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-secondary {
  padding: 8px 24px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:hover {
  background: #a6a9ad;
}

/* ========================================== */
/* مودال استعلام */
/* ========================================== */
.verify-status {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.verify-success {
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  color: #67c23a;
}

.verify-failed {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  color: #e6a23c;
}

.verify-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.verify-card h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 14px;
}

.verify-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
  color: #606266;
}

.verify-row .value {
  color: #2c3e50;
  font-weight: 500;
}

.verify-message {
  margin-top: 8px;
  padding: 6px 10px;
  background: #fef0f0;
  border-radius: 4px;
  font-size: 12px;
  color: #f56c6c;
}

.text-success {
  color: #67c23a;
}

.text-danger {
  color: #f56c6c;
}

/* ========================================== */
/* مودال رد */
/* ========================================== */
.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 6px;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
}

.form-textarea:focus {
  outline: none;
  border-color: #409eff;
}

.help-text {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: block;
}

/* ========================================== */
/* مودال عکس */
/* ========================================== */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s;
}

.image-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.image-modal-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
}

.image-close {
  position: absolute;
  top: -40px;
  left: -10px;
  background: none;
  border: none;
  color: white;
  font-size: 30px;
  cursor: pointer;
  opacity: 0.7;
}

.image-close:hover {
  opacity: 1;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .bank-approvals {
    padding: 12px;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-input {
    width: 100%;
    min-width: unset;
  }

  .btn-search,
  .btn-reset {
    width: 100%;
    text-align: center;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .card-actions {
    flex-direction: column;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .btn-approve,
  .modal-footer .btn-secondary {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .bank-approvals {
    padding: 8px;
  }

  .page-header h2 {
    font-size: 20px;
  }
}
</style>